import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';

export const Events: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchGuestAndEvents = async () => {
            if (!user) return;
            setLoading(true);

            try {
                // Check if admin
                const idTokenResult = await user.getIdTokenResult();
                const isAdmin = idTokenResult.claims.role === 'admin';

                // 1. Fetch guest data (if it exists)
                const guestDoc = await getDoc(doc(db, 'guests', user.uid));
                const gData = guestDoc.exists() ? guestDoc.data() : { events: [], roles: [] };

                // 2. Fetch all events
                const q = query(collection(db, 'events'), orderBy('startTime', 'asc'));
                const sn = await getDocs(q);

                const filteredEvents = sn.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter((e: any) => {
                        if (isAdmin) return true; // Admins see everything

                        // Check event match
                        const hasEvent = (gData.events || []).includes(e.eventCode || e.celebration);
                        if (!hasEvent) return false;

                        // Check role match if restricted
                        if (e.visibilityRoles && e.visibilityRoles.length > 0) {
                            const hasRole = gData.roles?.some((r: string) => e.visibilityRoles.includes(r));
                            if (!hasRole) return false;
                        }

                        return true;
                    });

                setEvents(filteredEvents);
            } catch (err) {
                console.error("Error fetching events:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchGuestAndEvents();
    }, [user]);

    if (loading) return (
        <div className="min-h-[60vh] flex items-center justify-center">
            <div className="animate-pulse text-secondary-light font-serif text-xl">{t('common.loading')}</div>
        </div>
    );

    const lang = i18n.language.startsWith('es') ? 'es' : 'en';

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 font-sans">
            <header className="mb-12 text-center">
                <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 tracking-tight">
                    {t('nav.events')}
                </h1>
                <div className="w-24 h-1 bg-accent/20 mx-auto rounded-full" />
            </header>

            <div className="space-y-12 relative">
                {/* Vertical Line for Itinerary */}
                <div className="absolute left-0 md:left-1/2 top-4 bottom-4 w-px bg-gray-100 hidden md:block" />

                {events.map((e, idx) => {
                    const date = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
                    const isEven = idx % 2 === 0;

                    return (
                        <div key={e.id} className={`flex flex-col md:flex-row items-center gap-8 relative ${isEven ? 'md:flex-row-reverse' : ''}`}>
                            {/* Dot on line */}
                            <div className="absolute left-0 md:left-1/2 top-10 w-4 h-4 rounded-full border-4 border-white shadow-sm bg-accent md:-ml-2 hidden md:block" />

                            <div className="w-full md:w-1/2 text-center md:text-left">
                                <div className={`p-8 bg-white rounded-3xl shadow-card border border-gray-100 transition-all hover:shadow-lg ${isEven ? 'md:text-right' : 'md:text-left'}`}>
                                    <div className="flex items-center gap-2 mb-4 justify-center md:justify-start flex-wrap">
                                        <span className="text-sm font-black uppercase tracking-widest text-accent bg-accent/5 px-2 py-1 rounded">
                                            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${e.eventCode === 'IE' || e.celebration === 'IE' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-primary-light/30 text-primary-dark border-primary-light/50'}`}>
                                            {(e.eventCode === 'IE' || e.celebration === 'IE') ? 'Ireland' : (e.eventCode === 'PB' || e.celebration === 'PB') ? 'Pre-Boda' : 'Spain'}
                                        </span>
                                    </div>

                                    <h3 className="text-2xl font-serif text-secondary mb-2 group-hover:text-primary transition-colors">
                                        {e.title?.[lang] || e.title?.en}
                                    </h3>

                                    <p className="text-secondary-light text-base mb-4 font-medium italic">
                                        {e.locationName}
                                    </p>

                                    {e.address && (
                                        <div className="text-sm text-secondary-light/60 mb-4 bg-bg-subtle p-3 rounded-xl border border-gray-50 flex items-start gap-2">
                                            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span className="leading-relaxed">{e.address}</span>
                                        </div>
                                    )}

                                    {e.notes?.[lang] && (
                                        <p className="text-base text-secondary-light leading-relaxed border-t border-gray-50 pt-4 mt-4">
                                            {e.notes[lang]}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="hidden md:block w-1/2" />
                        </div>
                    );
                })}

                {events.length === 0 && (
                    <div className="text-center p-12 bg-white rounded-3xl border border-dashed border-gray-200">
                        <p className="text-secondary-light italic">No events found for your invitation. Please check back later.</p>
                    </div>
                )}
            </div>

            <footer className="mt-16 text-center">
                <p className="text-sm text-secondary-light/40 uppercase tracking-widest font-bold">
                    All times listed in local time
                </p>
            </footer>
        </div>
    );
};
